/**
 * Users API wrapper (DNKADM-0162 + DNKADM-0166 envelope fix + DNKADM-0168 invites
 * + DNKADM-0255 account-state signal + DNKADM-0367 customers-only roster).
 *
 * DNKADM-0367: this roster is customers ONLY, by construction — staff logins never
 * appear here, they live under `/v2/admin/platform-members`. dink-api does not emit
 * `kind` on these rows at all, so it is the client-side constant `'CUSTOMER'`; only
 * `state` (`GUEST`/`USER`) is read from the wire. `role` is DERIVED for display from
 * `state` (see `@/domain/rosterKind`) and is never defaulted to `'USER'` — a
 * post-flip customer row carries no role at all, and inventing one would misfile
 * every customer on the screen.
 *
 * Lists the tenant-wide login roster from `GET /v2/admin/users`, enriched by
 * membership state and (DNKADM-0261) the server-computed `claimed` account
 * signal. `claimed: false` means the row is an unclaimed guest stub; `true`
 * only means "not an unclaimed guest stub", not that the person can sign in.
 * The field is always present on this endpoint and is not a filter; a roster
 * where every row is `true` is expected during the upstream deploy window. A
 * missing or malformed value still parses to `null` and falls through to
 * membership-only labeling. Also (DNKADM-0168) the USER-invitation write surface under
 * `/v2/admin/users/invitations`. All bearer-only `/v2/admin/**` calls: no cookies
 * and no CSRF, tenant server-derived from the JWT; the shared request client
 * handles bearer attachment, the mutating `X-Session-Mode` audit stamp, and the
 * bounded 401→refresh→retry.
 */
import { parseCustomerState, rosterDisplayRole, type CustomerState } from '@/domain/rosterKind';
import { request } from './client';

const PATH = '/v2/admin/users';
const INVITATIONS_PATH = '/v2/admin/users/invitations';
const PLATFORM_MEMBERS_PATH = '/v2/admin/platform-members';

/**
 * Known roster roles, widened to tolerate an unknown future value. `STAFF` is the new
 * floor role (DNKADM-0360); `SUPER_ADMIN` stays known so existing rows still badge.
 */
export type UserRole =
  | 'OWNER'
  | 'SUPER_ADMIN'
  | 'ADMIN'
  | 'STAFF'
  | 'USER'
  | 'GUEST'
  | (string & {});
type UserStatus = 'ACTIVE' | 'INVITED' | 'DISABLED' | 'UNKNOWN' | (string & {});
type MembershipState = 'USER' | 'ACTIVE_MEMBER' | 'INACTIVE_MEMBER' | (string & {});
export type UsersStateFilter = 'ALL' | 'ACTIVE' | 'INACTIVE';

export interface UserRow {
  /** The login's identifier. This roster is customers only (DNKADM-0367), so it is unique. */
  userId: string;
  name: string | null;
  email: string | null;
  /**
   * The role to DISPLAY (`rosterDisplayRole`): the wire role when there is one (the
   * transition window), else the row's `state`. `''` when neither is known. NEVER
   * defaulted to `'USER'` (DNKADM-0360).
   */
  role: UserRole;
  status: UserStatus;
  /** Always `'CUSTOMER'` — this roster is customers only by construction (DNKADM-0367);
   *  staff logins live under `/v2/admin/platform-members`. Not read from the wire. */
  kind: 'CUSTOMER';
  /** This row's account state (`GUEST`/`USER`); `null` only on a malformed row. */
  state: CustomerState;
  contactId: string | null;
  membershipState: MembershipState;
  /**
   * Server-computed unclaimed-stub signal (DNKADM-0261). `false` marks a guest-booking
   * login that was never claimed and has no password. `true` means only "not an
   * unclaimed guest stub"; it is NOT a promise that the person can sign in because
   * the upstream predicate is a three-way AND. `null` means absent/odd-typed on the
   * wire and must not be treated as `false`.
   */
  claimed: boolean | null;
  /**
   * Whether the person has confirmed their email (DNKAPI-0569, passed through from
   * saas-auth). `null` means unknown (absent/odd-typed, or an upstream predating the
   * field) and must never be shown as unconfirmed. An unclaimed guest stub normally
   * reads `false`; the roster badge gates on a login, so it never shows there.
   */
  emailVerified: boolean | null;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** A trimmed non-empty string, or null. */
function asText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

/** A stable string id from a number or non-empty string. */
function asId(value: unknown): string | null {
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
  return asText(value);
}

/** A real boolean, or null — never coerced (absent must stay "unknown", DNKADM-0261). */
function asBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function parseUserRow(raw: unknown): UserRow | null {
  if (!isObject(raw)) return null;
  const userId = asId(raw['userId']);
  if (userId === null) return null;
  // DNKADM-0367: this roster is customers only by construction — `kind` is not on the
  // wire, so it's the constant below. `state` is parsed directly; the displayed role is
  // DERIVED from it when the wire omits `role` (a post-flip customer row carries none),
  // never invented as 'USER'.
  const state = parseCustomerState(raw['state']);
  return {
    userId,
    name: asText(raw['name']),
    email: asText(raw['email']),
    role: rosterDisplayRole({ role: asText(raw['role']), kind: 'CUSTOMER', state }),
    status: asText(raw['status']) ?? 'UNKNOWN',
    kind: 'CUSTOMER',
    state,
    contactId: asId(raw['crmContactId']), // DNKAPI-0294 users-roster wire key mapped to our contactId model field.
    membershipState: asText(raw['membershipState']) ?? 'USER',
    // DNKADM-0261 account signal: `claimed` at every hop, by dink-api's request.
    // Only explicit boolean false marks an unclaimed guest stub; malformed values
    // stay null and never become a false accusation.
    claimed: asBoolean(raw['claimed']),
    // DNKADM-0410: only an explicit boolean is a signal; absent/malformed stays null.
    emailVerified: asBoolean(raw['emailVerified']),
  };
}

function parseUsers(raw: unknown): UserRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseUserRow).filter((row): row is UserRow => row !== null);
}

/**
 * `GET /v2/admin/users?state=ACTIVE|INACTIVE` → tenant login roster array.
 * The default/all state intentionally omits the query param.
 */
export async function listUsers(state: UsersStateFilter = 'ALL'): Promise<UserRow[]> {
  const body =
    state === 'ALL'
      ? await request<unknown>('GET', PATH)
      : await request<unknown>('GET', PATH, { query: { state } });
  const rows = Array.isArray(body) ? body : isObject(body) ? body['users'] : undefined;
  return parseUsers(rows);
}

// --- Orphaned-login CRM contact heal (DNKADM-0204) -----------------------------
//
// `POST /v2/admin/platform-members/{userId}/crm-contact/heal` (DNKAPI-0346) repairs a
// login whose contact link is missing/dangling. Idempotent, NO request body, bearer-only
// `/v2/admin/**` (tenant server-derived from the JWT — never send a tenant/location).
// It is keyed by the Users-roster row's `userId`, which is why it lives beside
// `listUsers` rather than in `platformMembers.ts`.

/** Known heal outcomes — widened so an unknown future status degrades instead of crashing. */
type HealStatus = 'HEALED' | 'VERIFIED' | 'NOT_CONFIGURED' | (string & {});

/**
 * One heal outcome. `HEALED` (a contact was created/relinked) and `VERIFIED` (the stored
 * id was already correct) are equivalent to callers; `NOT_CONFIGURED` means the tenant has
 * no active CRM integration — nothing to link, NOT an error. The raw CRM-contact wire key
 * is mapped to the model `contactId` at the parse boundary (dink-admin's identifier
 * contract), and `tagged` is informational.
 */
export interface HealResult {
  userId: string;
  status: HealStatus;
  contactId: string | null;
  tagged: boolean;
}

/**
 * Parse a heal response defensively (mirrors {@link parseUserRow}): a missing/odd-typed
 * field degrades rather than throwing. `userId` falls back to the requested id, and an
 * absent/unknown `status` is preserved raw for the caller's conservative branch.
 */
function parseHealResult(body: unknown, requestedUserId: string): HealResult {
  const raw: Record<string, unknown> = isObject(body) ? body : {};
  return {
    userId: asId(raw['userId']) ?? requestedUserId,
    status: asText(raw['status']) ?? '',
    contactId: asId(raw['crmContactId']), // DNKAPI-0346 heal wire key mapped to our contactId model field.
    tagged: raw['tagged'] === true,
  };
}

/**
 * `POST /v2/admin/platform-members/{userId}/crm-contact/heal` → the {@link HealResult}.
 * Sends NO body; bearer-only, tenant server-derived. Safe to call repeatedly (idempotent —
 * no duplicate contacts). Throws a typed `ApiError` on non-2xx (`403`, `404
 * MEMBER_NOT_FOUND`, `502 UPSTREAM_UNAVAILABLE` — the business code lands on
 * `ApiError.details.code`), mapped for display by `toResyncContactErrorView`
 * (`src/features/members/membersError.ts`); `401` follows the standard interceptor path.
 */
export async function healMemberContact(userId: string): Promise<HealResult> {
  const body = await request<unknown>(
    'POST',
    `${PLATFORM_MEMBERS_PATH}/${encodeURIComponent(userId)}/crm-contact/heal`,
  );
  return parseHealResult(body, userId);
}

// --- USER-invitation write surface (DNKADM-0168) -------------------------------
//
// The Users-screen invite experience: any admin may invite (no OWNER gate — the
// screen is already admin-gated) and the server FORCES the role to `USER` (no role
// field in the request). Bearer-only `/v2/admin/**` calls via `request()`; tenant is
// server-derived from the JWT — NEVER send a tenant. A non-2xx propagates as a typed
// `ApiError`; the v2 business `code` lands on `ApiError.details.code` (see
// `apiErrorFromBody` in `client.ts`) and is mapped by `toUserInviteErrorView`
// (`src/features/members/membersError.ts`).

/** Known USER-invitation lifecycle states (DNKADM-0168). */
type KnownUserInvitationStatus = 'PENDING' | 'ACCEPTED' | 'REVOKED' | 'EXPIRED';
/** Invitation status on the wire — widened to tolerate an unknown future enum. */
export type UserInvitationStatus = KnownUserInvitationStatus | (string & {});

/**
 * One USER invitation. The server forces `role` to `USER`; the wire's `tenantId`,
 * `inviteUrl`, and `emailSent` are intentionally ignored (the branded email is sent
 * server-side). `invitationId` normalizes to a string; `email` and the timestamps
 * degrade to `null`; `status` is preserved RAW so an unknown future enum renders
 * rather than crashing.
 */
export interface UserInvitation {
  invitationId: string;
  email: string | null;
  role: UserRole;
  status: UserInvitationStatus;
  createdAt: string | null;
  expiresAt: string | null;
}

/**
 * Reconcile a session-written invitation with the row's fetched one (DNKADM-0304 U1).
 *
 * The invite/resend WRITE responses are the trimmed dink-api
 * `PlatformInvitationResponse` — `{ invitationId, email, role, emailSent }`, no
 * `createdAt`/`expiresAt`/`status` — so an optimistic overlay built from one
 * parses those fields to null and a row invited this session renders
 * `Invited —` even though the fetched PENDING list carries the date. This merge
 * makes the two sources interchangeable by construction: the overlay's non-null
 * fields win (a server that later returns `createdAt` on the write response —
 * including a CHANGED value — wins automatically), and its null/blank fields
 * fill from the fetched row. Verified against dink-api: a resend rotates the
 * token and resets `expiresAt` but keeps the original `createdAt`, so filling
 * the overlay's null `createdAt` from the fetched row shows exactly the
 * original invite date.
 */
export function reconcileInvitation(
  overlay: UserInvitation,
  fetched: UserInvitation | null,
): UserInvitation {
  if (fetched === null) return overlay;
  return {
    ...overlay,
    email: overlay.email ?? fetched.email,
    status: overlay.status !== '' ? overlay.status : fetched.status,
    createdAt: overlay.createdAt ?? fetched.createdAt,
    expiresAt: overlay.expiresAt ?? fetched.expiresAt,
  };
}

/**
 * Parse one invitation row defensively (mirrors {@link parseUserRow}). This is the
 * customer-invite surface (DNKADM-0367) — same rule as the roster: `kind` is the
 * constant `'CUSTOMER'`, `state` parsed directly, and an absent role is UNKNOWN (`''`)
 * — never invented as `'USER'`. (This surface is USER-only server-side, and nothing
 * renders an invitation's role here.)
 */
function parseUserInvitation(raw: unknown): UserInvitation {
  const o = isObject(raw) ? raw : {};
  return {
    invitationId: asId(o['invitationId']) ?? '',
    email: asText(o['email']),
    role: rosterDisplayRole({
      role: asText(o['role']),
      kind: 'CUSTOMER',
      state: parseCustomerState(o['state']),
    }),
    status: asText(o['status']) ?? '',
    createdAt: asText(o['createdAt']),
    expiresAt: asText(o['expiresAt']),
  };
}

/**
 * Tolerantly extract the invitation array from a list body: a bare array or an
 * `invitations` wrapper. A non-array (or missing) yields `[]` rather than a throw.
 */
function extractInvitations(body: unknown): unknown[] {
  if (Array.isArray(body)) return body;
  if (isObject(body) && Array.isArray(body['invitations'])) return body['invitations'];
  return [];
}

function invitationPath(invitationId: string): string {
  return `${INVITATIONS_PATH}/${encodeURIComponent(invitationId)}`;
}

/**
 * Input for {@link inviteUser}. The FE model keeps the `contactId` name; it maps to
 * the CRM-contact invite-body wire field at the request boundary (below). There is
 * deliberately NO role field — the server forces `USER`.
 */
export interface InviteUserInput {
  email: string;
  name?: string;
  /** Optional booking-contact link, bound on acceptance (best-effort, server-side). */
  contactId?: string;
}

/**
 * `POST /v2/admin/users/invitations` ← `{ email, name?, <crm-contact>? }` → the created
 * {@link UserInvitation} (`role` will be `USER`). NO role field is sent (the server
 * forces `USER`); `name` and the contact link are sent only when provided. The email
 * is dispatched server-side (the response's `inviteUrl`/`tenantId` are ignored).
 * Throws a typed `ApiError` (e.g. `400 EMAIL_REQUIRED`, `409 ALREADY_ACTIVE_MEMBER` /
 * `INVITATION_ALREADY_PENDING`) mapped by `toUserInviteErrorView`.
 */
export async function inviteUser(input: InviteUserInput): Promise<UserInvitation> {
  const json: Record<string, unknown> = { email: input.email };
  if (input.name !== undefined) json['name'] = input.name;
  // Map the model `contactId` to the sanctioned CRM-contact invite-body wire field
  // (DNKADM-0168; the one line-shape sanctioned in contactIdNaming.guard.test.ts) —
  // sent ONLY when provided. Honest mapping, never obfuscated.
  if (input.contactId !== undefined) {
    json['crmContactId'] = input.contactId;
  }
  const body = await request<unknown>('POST', INVITATIONS_PATH, { json });
  return parseUserInvitation(body);
}

/**
 * `GET /v2/admin/users/invitations[?status=…]` → the tenant's USER invitations
 * (already USER-only server-side — no client filter). `status`
 * (`PENDING|ACCEPTED|REVOKED|EXPIRED`) is forwarded ONLY when set; omitted returns
 * all. Throws a typed `ApiError` on non-2xx.
 */
export async function listUserInvitations(
  status?: UserInvitationStatus,
): Promise<UserInvitation[]> {
  const options = status ? { query: { status } } : undefined;
  const body = await request<unknown>('GET', INVITATIONS_PATH, options);
  return extractInvitations(body).map(parseUserInvitation);
}

/**
 * `DELETE /v2/admin/users/invitations/{invitationId}` → `204`. Resolves to `void`.
 * A non-USER invitation id surfaces `404` (no existence leak). Throws a typed
 * `ApiError` on non-2xx.
 */
export async function cancelUserInvitation(invitationId: string): Promise<void> {
  await request<unknown>('DELETE', invitationPath(invitationId));
}

/**
 * `POST /v2/admin/users/invitations/{invitationId}/resend` → a FRESH
 * {@link UserInvitation} (rotates the token, resets expiry, re-sends the email);
 * replace the row with the returned invitation. A non-USER id surfaces `404`. Throws
 * a typed `ApiError` on non-2xx.
 */
export async function resendUserInvitation(invitationId: string): Promise<UserInvitation> {
  const body = await request<unknown>('POST', `${invitationPath(invitationId)}/resend`);
  return parseUserInvitation(body);
}
